import React from 'react'

interface ClusterNodeProps {
  data: {
    count: number
    color: string
    isExpanded?: boolean
    isHidden?: boolean
  }
  id: string
  selected?: boolean
}

const ClusterNode: React.FC<ClusterNodeProps> = ({ data, id, selected }) => {
  const size = Math.min(Math.max(12 + Math.log(data.count) * 4, 16), 40) // Size based on count
  const isExpanded = data.isExpanded || false
  const isHidden = data.isHidden || false
  const isSelected = selected || false

  // If cluster is hidden (expanded), make it transparent or very small
  if (isHidden) {
    return (
      <div
        style={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          border: isSelected ? '1px solid #fbbf24' : '1px dashed rgba(251, 191, 36, 0.5)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isSelected ? 0.8 : 0.3,
          transition: 'all 0.2s ease',
          position: 'relative',
          boxShadow: isSelected ? '0 0 6px rgba(251, 191, 36, 0.6)' : 'none',
        }}
        title={`${data.count} items expanded - Click to collapse`}
      >
        {/* Small indicator that cluster is expanded */}
        <div
          style={{
            position: 'absolute',
            width: 2,
            height: 2,
            borderRadius: '50%',
            backgroundColor: '#fbbf24',
          }}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: data.color,
        border: isSelected
          ? '3px solid #fbbf24'
          : isExpanded
          ? '3px solid #fbbf24'
          : '2px solid rgba(255, 255, 255, 0.8)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(8, size * 0.3),
        fontWeight: 'bold',
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
        boxShadow: isSelected
          ? '0 0 16px rgba(251, 191, 36, 0.8), 0 2px 8px rgba(0, 0, 0, 0.3)'
          : isExpanded
          ? '0 0 12px rgba(251, 191, 36, 0.6), 0 2px 8px rgba(0, 0, 0, 0.3)'
          : '0 2px 8px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.2s ease',
        position: 'relative',
        transform: isSelected ? 'scale(1.15)' : isExpanded ? 'scale(1.1)' : 'scale(1)',
      }}
      title={`${data.count} items clustered - Click to ${isExpanded ? 'collapse' : 'expand'}${
        isSelected ? ' (Selected)' : ''
      }`}
    >
      {data.count > 99 ? '99+' : data.count}
      {/* Subtle pulse animation for large clusters */}
      {data.count > 50 && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            animation: 'pulse 2s infinite',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Selection indicator */}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: '-10%',
            left: '-10%',
            width: '120%',
            height: '120%',
            borderRadius: '50%',
            border: '2px solid #fbbf24',
            animation: 'pulse 1.5s infinite',
            pointerEvents: 'none',
            opacity: 0.7,
          }}
        />
      )}
      {/* Expansion indicator */}
      {isExpanded && (
        <div
          style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#fbbf24',
            border: '1px solid #ffffff',
            fontSize: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000000',
            fontWeight: 'bold',
          }}
        >
          ×
        </div>
      )}
    </div>
  )
}

export default ClusterNode
